/**
 * Master data — `master-data.html`, §10 of docs/API.md, requirement §54.
 *
 * Twenty-one reference tables, one screen, **zero hard-coded forms**. Every
 * column, input type, enum and grid width comes from
 * `GET /master-data/entities`, which exists for exactly this reason: the field
 * catalogue is generated from the same registry the API validates against, so a
 * column added server-side appears here without a front-end change, and a form
 * cannot offer a field the database will reject.
 *
 * That is also why this screen does not use the `Card` / `DataTable` helpers.
 * The prototype's two-pane `.tpl-layout` — a grouped entity list beside a record
 * editor — is a specific arrangement `app.css` already describes, and rebuilding
 * it out of generic containers would put it back at arm's length from the design.
 */
import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as api from '../../api/masterData.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { pushToast } from '../ui/uiSlice.js';
import {
  Spinner, ErrorState, PageLoader, SkeletonTable, Skeleton,
} from '../../components/common/index.jsx';
import { number } from '../../utils/formatters.js';

/** Master-data tables are picklists; 200 covers every one of them in a normal tenant. */
const PAGE_LIMIT = 200;

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

export default function MasterDataPage() {
  const { can } = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const catalogue = useQuery({ queryKey: queryKeys.masterData.entities, queryFn: api.listEntities });
  const groups = catalogue.data?.groups ?? [];
  const allEntities = useMemo(() => groups.flatMap((g) => g.entities), [groups]);

  /* The entity lives in the URL query as `?e=`, as the prototype does, so a
     particular list can be linked to and survives a reload. */
  const [entityKey, setEntityKey] = useState(() => new URLSearchParams(window.location.search).get('e') ?? '');
  const entity = allEntities.find((e) => e.key === entityKey) ?? allEntities[0] ?? null;

  useEffect(() => {
    if (!entity) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('e') !== entity.key) {
      url.searchParams.set('e', entity.key);
      window.history.replaceState(null, '', url);
    }
  }, [entity]);

  const [entityFilter, setEntityFilter] = useState('');
  const [rowSearch, setRowSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [scopeId, setScopeId] = useState('');
  const debouncedSearch = useDebounce(rowSearch, 250);

  /* A scope chosen for one entity means nothing for the next. */
  useEffect(() => { setScopeId(''); setRowSearch(''); }, [entity?.key]);

  const listParams = {
    limit: PAGE_LIMIT,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    ...(activeOnly ? { is_active: 'true' } : {}),
    ...(scopeId ? { scope_id: scopeId } : {}),
  };

  const records = useQuery({
    queryKey: queryKeys.masterData.list(entity?.key, listParams),
    queryFn: () => api.listRecords(entity.key, listParams),
    enabled: Boolean(entity),
    /**
     * Keep the last result **only while the entity is the same.**
     *
     * A bare `(prev) => prev` held it across an entity switch too, and that is
     * worse than a plain wait: `data` was defined, so `isLoading` stayed false
     * and the skeleton below never rendered — for the whole round trip the pane
     * showed the *previous* table's rows under the *new* entity's headers, every
     * cell an em-dash because the keys do not match. Switching from Lead
     * Statuses to Currencies read as "Currencies has seven blank rows", not as
     * "still loading". The footer counted the old rows and Export would have
     * written them out under the new entity's filename.
     *
     * Scoped this way the placeholder still does the job it is actually for —
     * typing in the search box, toggling Active only, changing scope, all
     * within one entity, where the table must not collapse under the cursor —
     * and an entity switch falls through to `isLoading`, which draws the
     * skeleton at the new entity's own width.
     *
     * `queryKey[2]` is the entity: `['master-data', 'list', entity, params]`.
     */
    placeholderData: (prev, prevQuery) =>
      (prevQuery?.queryKey?.[2] === entity?.key ? prev : undefined),
  });

  const [formState, setFormState] = useState(null);   // { mode, record }
  const [deleting, setDeleting] = useState(null);     // the record awaiting a reason

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.masterData.all });
  };

  const toastError = (verb) => (error) => dispatch(pushToast({
    tone: 'danger',
    message: `Could not ${verb}`,
    /* The API's refusals are written to be read: `master_data_in_use` names the
       counts, `stale_write` names the version. Passing the message straight
       through beats a generic "something went wrong". */
    detail: error.message,
  }));

  const totalRecords = allEntities.reduce((n, e) => n + e.record_count, 0);

  if (catalogue.isLoading) return <PageLoader label="Loading the master-data catalogue" />;
  if (catalogue.error) return <ErrorState error={catalogue.error} onRetry={catalogue.refetch} />;

  return (
    <>
      <div className="lv-head mb-3">
        <div className="hl-icon" style={{ background: 'linear-gradient(135deg,#8b5cf6,#d946ef)' }}>
          <i className="bi bi-database-fill-gear" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="crumb">
            <Link to="/">Administration</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" />
            <span>Master data</span>
          </div>
          <h1 className="page-title">Master Data</h1>
          <div className="t-xs ink-3 mt-1">
            {allEntities.length} reference tables · {number(totalRecords)} records ·
            {' '}changes apply across every module
          </div>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-soft btn-sm-x"
            onClick={() => exportCsv(entity, records.data?.data ?? [], dispatch)}
            disabled={!entity || !(records.data?.data ?? []).length}
          >
            <i className="bi bi-download me-1" aria-hidden="true" />Export
          </button>
          {can('admin.master', 'create') && entity && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setFormState({ mode: 'create' })}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />New record
            </button>
          )}
        </div>
      </div>

      <div className="card-x fade-up">
        <div className="tpl-layout">
          <EntityPicker
            groups={groups}
            current={entity}
            filter={entityFilter}
            onFilter={setEntityFilter}
            onSelect={(key) => setEntityKey(key)}
          />

          <div className="tpl-editor">
            {entity && (
              <>
                <EditorToolbar
                  entity={entity}
                  allEntities={allEntities}
                  search={rowSearch}
                  onSearch={setRowSearch}
                  activeOnly={activeOnly}
                  onActiveOnly={setActiveOnly}
                  scopeId={scopeId}
                  onScope={setScopeId}
                  canCreate={can('admin.master', 'create')}
                  onNew={() => setFormState({ mode: 'create' })}
                />

                {records.isLoading ? (
                  <SkeletonTable rows={8} columns={skeletonColumns(entity)} />
                ) : records.error ? (
                  <div className="p-3"><ErrorState error={records.error} onRetry={records.refetch} /></div>
                ) : (
                  /* Same entity, new filter: the rows on screen are last
                     request's, held on purpose so the table does not collapse
                     under the cursor mid-keystroke. Say so — otherwise a search
                     that returns the same count looks like nothing happened. */
                  <div className={records.isPlaceholderData ? 'tbl-busy' : undefined}
                       aria-busy={records.isPlaceholderData || undefined}>
                  <RecordTable
                    entity={entity}
                    rows={records.data?.data ?? []}
                    canEdit={can('admin.master', 'edit')}
                    canDelete={can('admin.master', 'delete')}
                    scopeId={scopeId}
                    filtered={Boolean(debouncedSearch || activeOnly)}
                    onEdit={(record) => setFormState({ mode: 'edit', record })}
                    onDuplicate={(record) => setFormState({ mode: 'duplicate', record })}
                    onDelete={(record) => setDeleting(record)}
                    onChanged={invalidate}
                    onError={toastError('save that change')}
                    onNew={() => setFormState({ mode: 'create' })}
                  />
                  </div>
                )}

                <EditorFoot
                  entity={entity}
                  page={records.data?.page}
                  rows={records.data?.data ?? []}
                  loading={records.isLoading}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {formState && entity && (
        <RecordFormModal
          entity={entity}
          allEntities={allEntities}
          mode={formState.mode}
          record={formState.record}
          scopeId={scopeId}
          onClose={() => setFormState(null)}
          onSaved={(record, again) => {
            invalidate();
            dispatch(pushToast({
              tone: 'success',
              message: `${entity.singular} "${record.name ?? record.code}" ${formState.mode === 'edit' ? 'updated' : 'created'}.`,
              detail: `Available across every module that uses ${entity.label.toLowerCase()}.`,
            }));
            setFormState(again ? { mode: 'create' } : null);
          }}
        />
      )}

      {deleting && entity && (
        <DeleteModal
          entity={entity}
          record={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            invalidate();
            dispatch(pushToast({
              tone: 'info',
              message: `${entity.singular} "${deleting.name ?? deleting.code}" deleted.`,
            }));
            setDeleting(null);
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Entity picker                                                               */
/* -------------------------------------------------------------------------- */

function EntityPicker({ groups, current, filter, onFilter, onSelect }) {
  const q = filter.trim().toLowerCase();
  const shown = groups
    .map((g) => ({
      ...g,
      entities: g.entities.filter(
        (e) => !q || e.label.toLowerCase().includes(q) || g.name.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.entities.length > 0);

  return (
    <div className="tpl-list">
      <div className="p-2 border-bottom" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
        <div className="searchbox">
          <i className="bi bi-search" aria-hidden="true" />
          <input
            type="search"
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            placeholder="Find a reference table…"
            style={{ paddingRight: 12 }}
            aria-label="Find a reference table"
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="empty">
          <div className="e-ico"><i className="bi bi-search" aria-hidden="true" /></div>
          <div className="t-sm fw-650">Nothing matches</div>
        </div>
      ) : (
        shown.map((group) => (
          <div key={group.name}>
            <div
              className="px-3 py-2 t-2xs fw-700 tracking-wide text-uppercase ink-4"
              style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--line-2)' }}
            >
              {group.name}
            </div>
            {group.entities.map((e) => (
              <div
                key={e.key}
                className={`tpl-item ${e.key === current?.key ? 'active' : ''}`}
                onClick={() => onSelect(e.key)}
                onKeyDown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && onSelect(e.key)}
                role="button"
                tabIndex={0}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className={`bi ${e.icon} text-primary`} aria-hidden="true" />
                  <div className="min-w-0 flex-grow-1">
                    <div className="tn text-truncate">{e.label}</div>
                    <div className="ts">{e.active_count} active of {e.record_count}</div>
                  </div>
                  <span className="pill pill-gray">{e.record_count}</span>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Editor toolbar                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The scope selector only appears for entities the catalogue reports as scoped.
 *
 * `opportunity_stages` live under a pipeline and `sort_order` is unique per
 * pipeline, so a reorder must name one — which is why picking a scope is a
 * precondition for the ordering arrows rather than a convenience filter.
 */
function EditorToolbar({
  entity, allEntities, search, onSearch, activeOnly, onActiveOnly,
  scopeId, onScope, canCreate, onNew,
}) {
  const scopeField = entity.scoped_by
    ? entity.fields.find((f) => f.key === entity.scoped_by)
    : null;
  const scopeEntity = scopeField?.ref ? allEntities.find((e) => e.key === scopeField.ref) : null;

  const scopeOptions = useQuery({
    queryKey: queryKeys.masterData.options(scopeEntity?.key),
    queryFn: () => api.listRecords(scopeEntity.key, { limit: PAGE_LIMIT, is_active: 'true', with_usage: 'false' }),
    enabled: Boolean(scopeEntity),
  });

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 p-3 border-bottom">
      <div className="hl-icon" style={{ width: 32, height: 32, fontSize: 15, background: 'var(--grad-brand)' }}>
        <i className={`bi ${entity.icon}`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <div className="t-md fw-700">{entity.label}</div>
        <div className="t-2xs ink-3">
          {entity.fields.length} fields
          {entity.ordered ? ' · ordered' : ''}
          {entity.soft_delete ? ' · soft delete' : ''}
        </div>
      </div>

      <div className="ms-auto d-flex flex-wrap align-items-center gap-2">
        {scopeEntity && (
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 170 }}
            value={scopeId}
            onChange={(e) => onScope(e.target.value)}
            aria-label={scopeField.label}
          >
            <option value="">All {scopeEntity.label.toLowerCase()}</option>
            {(scopeOptions.data?.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.name ?? r.code}</option>
            ))}
          </select>
        )}

        <div className="searchbox" style={{ maxWidth: 190 }}>
          <i className="bi bi-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search records…"
            style={{ paddingRight: 12 }}
            aria-label="Search records"
          />
        </div>

        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="mdActiveOnly"
            checked={activeOnly}
            onChange={(e) => onActiveOnly(e.target.checked)}
          />
          <label className="form-check-label t-xs" htmlFor="mdActiveOnly">Active only</label>
        </div>

        {canCreate && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={onNew}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New
          </button>
        )}
      </div>
    </div>
  );
}

function EditorFoot({ entity, page, rows, loading }) {
  const active = rows.filter((r) => r.is_active !== false).length;
  return (
    <div className="card-foot d-flex flex-wrap align-items-center gap-2">
      {/* Not "0 records · 0 active" while the request is out. That is a
          statement about the entity, and it is false for most of them — the
          count arrives a moment later and corrects itself, which reads as the
          table having been empty and then not. */}
      {loading ? (
        <Skeleton w={160} h={10} />
      ) : (
        <span className="t-xs ink-3">
          {number(page?.total ?? rows.length)} records · {number(active)} active on this page
        </span>
      )}
      <span className="t-2xs ink-4 ms-auto">
        {entity.ordered
          ? 'Use the arrows to change the order users see.'
          : 'Inactive records stay on historical data but are hidden from pickers.'}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Record table                                                                */
/* -------------------------------------------------------------------------- */

const NUMERIC = new Set(['number', 'percent', 'currency']);

/** Render one cell from the field descriptor rather than from the column name. */
function Cell({ entity, row, column }) {
  const field = entity.fields.find((f) => f.key === column) ?? { key: column, type: 'text' };
  const raw = row[column];
  const dash = <span className="ink-4">—</span>;

  switch (field.type) {
    case 'switch':
      return raw
        ? <span className="pill pill-green"><i className="bi bi-check-lg" aria-hidden="true" />Yes</span>
        : <span className="pill pill-gray">No</span>;
    case 'color':
      return (
        <span className="d-inline-flex align-items-center gap-2">
          <span className="dot-ind" style={{ width: 12, height: 12, background: raw || '#cbd5e1' }} />
          <span className="t-2xs ink-4">{raw || ''}</span>
        </span>
      );
    case 'ref': {
      const label = row.labels?.[column];
      return label ? <span className="pill pill-gray">{label}</span> : dash;
    }
    case 'percent':
      return raw == null ? dash : `${raw}%`;
    case 'currency':
      return raw == null ? dash : `${row.currency ?? ''} ${number(raw)}`.trim();
    case 'code':
      return raw ? <code>{raw}</code> : dash;
    case 'json':
      return raw && Object.keys(raw).length ? <span className="pill pill-gray">{Object.keys(raw).length} keys</span> : dash;
    default:
      return raw == null || raw === '' ? dash : String(raw);
  }
}

/**
 * The header row the skeleton stands in for.
 *
 * Derived from the same `entity.columns` the live table renders, so the two are
 * the same width by construction — the record table is the whole right-hand pane
 * here, and a spinner in its place collapsed the pane and shifted the entity
 * picker beside it every time the selection changed.
 */
function skeletonColumns(entity) {
  return [
    ...(entity.ordered ? [{ key: '__order', header: 'Order', width: 64 }] : []),
    ...entity.columns
      .filter((c) => c !== 'sort_order')
      .map((key) => ({ key, header: entity.fields.find((f) => f.key === key)?.label ?? key })),
    { key: '__actions', header: 'Actions', width: 118 },
  ];
}

function RecordTable({
  entity, rows, canEdit, canDelete, scopeId, filtered,
  onEdit, onDuplicate, onDelete, onChanged, onError, onNew,
}) {
  const columns = entity.columns.filter((c) => c !== 'sort_order');

  const toggleActive = useMutation({
    mutationFn: ({ row }) => api.updateRecord(entity.key, row.id, { is_active: !row.is_active }, row.row_version),
    onSuccess: onChanged,
    onError,
  });

  /**
   * Reordering needs the whole list in its current order, so it is refused
   * while a search or an Active-only filter is hiding rows: moving a row up past
   * a neighbour you cannot see would silently renumber the ones in between.
   */
  const reorder = useMutation({
    mutationFn: ({ from, to }) => {
      const ids = rows.map((r) => r.id);
      const [moved] = ids.splice(from, 1);
      ids.splice(to, 0, moved);
      return api.reorderRecords(entity.key, ids, entity.scoped_by ? scopeId : undefined);
    },
    onSuccess: onChanged,
    onError,
  });

  const canReorder = entity.ordered && !filtered && (!entity.scoped_by || Boolean(scopeId));

  if (rows.length === 0) {
    return (
      <div className="empty">
        <div className="e-ico"><i className={`bi ${entity.icon}`} aria-hidden="true" /></div>
        <div className="t-md fw-650">No {entity.label.toLowerCase()} {filtered ? 'match these filters' : 'yet'}</div>
        <div className="t-sm ink-3">
          {filtered
            ? 'Try widening the search, or turn off Active only.'
            : 'Add the first record to make it available across the CRM.'}
        </div>
        {!filtered && (
          <button type="button" className="btn btn-add btn-sm-x mt-3" onClick={onNew}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New {entity.singular.toLowerCase()}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {entity.ordered && <th style={{ width: 64 }}>Order</th>}
            {columns.map((c) => {
              const f = entity.fields.find((x) => x.key === c);
              return <th key={c} className={NUMERIC.has(f?.type) ? 'num' : ''}>{f?.label ?? c}</th>;
            })}
            <th className="text-end" style={{ width: 118 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id} style={row.is_active === false ? { opacity: 0.55 } : undefined}>
              {entity.ordered && (
                <td>
                  <div className="d-inline-flex gap-1">
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22, fontSize: 10 }}
                      disabled={!canReorder || i === 0 || reorder.isPending}
                      title={canReorder ? 'Move up' : reorderHint(entity, filtered)}
                      onClick={() => reorder.mutate({ from: i, to: i - 1 })}
                      aria-label="Move up"
                    >
                      <i className="bi bi-chevron-up" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22, fontSize: 10 }}
                      disabled={!canReorder || i === rows.length - 1 || reorder.isPending}
                      title={canReorder ? 'Move down' : reorderHint(entity, filtered)}
                      onClick={() => reorder.mutate({ from: i, to: i + 1 })}
                      aria-label="Move down"
                    >
                      <i className="bi bi-chevron-down" aria-hidden="true" />
                    </button>
                  </div>
                </td>
              )}

              {columns.map((c) => {
                const f = entity.fields.find((x) => x.key === c);
                const strong = c === 'name' || (c === 'code' && columns[0] === 'code');
                return (
                  <td key={c} className={`${NUMERIC.has(f?.type) ? 'num' : ''} ${strong ? 'strong' : ''}`}>
                    {c === 'is_active' ? (
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={Boolean(row.is_active)}
                          disabled={!canEdit || toggleActive.isPending}
                          onChange={() => toggleActive.mutate({ row })}
                          aria-label={`Active — ${row.name ?? row.code}`}
                        />
                      </div>
                    ) : (
                      <Cell entity={entity} row={row} column={c} />
                    )}
                  </td>
                );
              })}

              <td className="text-end">
                <div className="row-actions d-inline-flex gap-1">
                  {canEdit && (
                    <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 12 }}
                            title="Edit" onClick={() => onEdit(row)}>
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                  )}
                  {canEdit && (
                    <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 12 }}
                            title="Duplicate" onClick={() => onDuplicate(row)}>
                      <i className="bi bi-files" aria-hidden="true" />
                    </button>
                  )}
                  {/* The reference count is only in this tooltip now that the
                      "Used by" column is gone. Keep it: the API refuses a delete
                      while anything points at the row, and the hover is the last
                      chance to say so before the 409. */}
                  {canDelete && (
                    <button type="button" className="icon-btn text-danger" style={{ width: 28, height: 28, fontSize: 12 }}
                            title={row.usage_count > 0 ? `In use by ${row.usage_count} record(s)` : 'Delete'}
                            onClick={() => onDelete(row)}>
                      <i className="bi bi-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function reorderHint(entity, filtered) {
  if (filtered) return 'Clear the search and Active only filter to reorder.';
  if (entity.scoped_by) return `Choose one ${entity.scoped_by.replace(/_id$/, '')} first — the order is set within it.`;
  return 'Reordering is not available here.';
}

/* -------------------------------------------------------------------------- */
/* Generated record form                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The form is built from `entity.fields`, so there is one of these rather than
 * twenty-one. `type` chooses the input, `col` its width, `options` a select's
 * choices, `ref` the entity to look up against, and `required`/`nullable` what
 * has to be filled in.
 */
function RecordFormModal({ entity, allEntities, mode, record, scopeId, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [values, setValues] = useState(() => initialValues(entity, mode, record, scopeId));
  const [fieldErrors, setFieldErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [again, setAgain] = useState(false);

  /* Ref fields need the referenced list. Fetched together rather than one query
     per rendered <select>, so opening the form is a single round of requests. */
  const refFields = entity.fields.filter((f) => f.type === 'ref' && f.ref);
  const refQueries = useQueries({
    queries: refFields.map((f) => ({
      queryKey: queryKeys.masterData.options(f.ref),
      queryFn: () => api.listRecords(f.ref, { limit: PAGE_LIMIT, is_active: 'true', with_usage: 'false' }),
      staleTime: 60_000,
    })),
  });
  const refOptions = Object.fromEntries(
    refFields.map((f, i) => [f.key, refQueries[i]?.data?.data ?? []]),
  );

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const save = useMutation({
    mutationFn: (payload) => (isEdit
      ? api.updateRecord(entity.key, record.id, payload, record.row_version)
      : api.createRecord(entity.key, payload)),
    onSuccess: (saved) => onSaved(saved, again),
    onError: (error) => {
      /* `details[]` names the offending fields, so they land on the inputs
         rather than in one opaque banner at the bottom. */
      setFieldErrors(error.fieldErrors?.() ?? {});
      setFailure(error);
    },
  });

  const submit = (e, saveAndNew) => {
    e.preventDefault();
    setAgain(Boolean(saveAndNew));
    setFieldErrors({});
    setFailure(null);
    save.mutate(buildPayload(entity, values, isEdit ? record : null));
  };

  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center gap-2">
                <div className="hl-icon" style={{ width: 32, height: 32, fontSize: 15, background: 'var(--grad-brand)' }}>
                  <i className={`bi ${entity.icon}`} aria-hidden="true" />
                </div>
                <div>
                  <h5 className="modal-title">{isEdit ? `Edit ${entity.singular}` : `New ${entity.singular}`}</h5>
                  <div className="t-xs ink-3">
                    {mode === 'duplicate'
                      ? `Duplicated from ${record.name ?? record.code}`
                      : isEdit ? `${entity.label} · version ${record.row_version}` : entity.label}
                  </div>
                </div>
              </div>
              <button type="button" className="btn-close btn-sm" aria-label="Close" onClick={onClose} />
            </div>

            <form onSubmit={(e) => submit(e, false)} noValidate>
              <div className="modal-body">
                <div className="row g-3">
                  {entity.fields.map((f) => (
                    <FormField
                      key={f.key}
                      field={f}
                      value={values[f.key]}
                      error={fieldErrors[f.key]}
                      options={refOptions[f.key]}
                      onChange={(v) => set(f.key, v)}
                    />
                  ))}
                </div>

                {failure && !failure.isValidation && (
                  <div className="alert alert-danger py-2 px-3 t-xs mt-3" role="alert">
                    <i className="bi bi-exclamation-circle me-1" aria-hidden="true" />
                    {failure.message}
                    {failure.code === 'stale_write' && (
                      <div className="mt-1">
                        Someone else saved version {failure.payload?.current_version} while this was open.
                        Close and reopen to edit the current record.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
                {!isEdit && (
                  <button type="submit" className="btn btn-save btn-sm-x" disabled={save.isPending}
                          onClick={(e) => submit(e, true)}>
                    Save &amp; New
                  </button>
                )}
                <button type="submit" className="btn btn-save btn-sm-x" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

/** One generated input, chosen by `field.type`. */
function FormField({ field, value, error, options, onChange }) {
  const id = `fld_${field.key}`;
  const col = `col-md-${field.col ?? 6}`;
  const invalid = error ? ' is-invalid' : '';

  if (field.type === 'switch') {
    return (
      <div className={`${col} d-flex align-items-end`}>
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id={id}
                 checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
          <label className="form-check-label" htmlFor={id}>{field.label}</label>
        </div>
      </div>
    );
  }

  const label = (
    <label className="lbl" htmlFor={id}>
      {field.label}
      {field.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
    </label>
  );
  const help = error
    ? <div className="t-2xs mt-1" style={{ color: 'var(--rose)' }}>{error}</div>
    : field.hint ? <div className="t-2xs ink-4 mt-1">{field.hint}</div> : null;

  let input;
  switch (field.type) {
    case 'textarea':
      input = <textarea className={`form-control${invalid}`} id={id} rows={2}
                        value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      input = (
        <select className={`form-select${invalid}`} id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">— None —</option>
          {(field.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      break;
    case 'ref':
      input = (
        <select className={`form-select${invalid}`} id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">— None —</option>
          {(options ?? []).map((r) => <option key={r.id} value={r.id}>{r.name ?? r.code}</option>)}
        </select>
      );
      break;
    case 'color':
      input = (
        <div className="d-flex gap-2">
          <input type="color" className="form-control form-control-color" id={id}
                 style={{ height: 37, padding: 4, width: 52 }}
                 value={value || '#6366f1'} onChange={(e) => onChange(e.target.value)} />
          <input type="text" className={`form-control${invalid}`} value={value ?? ''}
                 onChange={(e) => onChange(e.target.value)} placeholder="#6366f1" aria-label={`${field.label} hex`} />
        </div>
      );
      break;
    case 'number':
    case 'currency':
    case 'percent':
      input = (
        <div className="input-group">
          {field.type === 'currency' && <span className="input-group-text t-xs">¤</span>}
          <input type="number" className={`form-control${invalid}`} id={id} value={value ?? ''}
                 min={field.min} max={field.max} step={field.step ?? (field.type === 'number' ? 1 : 'any')}
                 onChange={(e) => onChange(e.target.value)} />
          {field.type === 'percent' && <span className="input-group-text t-xs">%</span>}
        </div>
      );
      break;
    case 'json':
      input = <textarea className={`form-control${invalid}`} id={id} rows={3} spellCheck={false}
                        value={value ?? ''} onChange={(e) => onChange(e.target.value)}
                        placeholder="{ }" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }} />;
      break;
    case 'date':
      input = <input type="date" className={`form-control${invalid}`} id={id} value={value ?? ''}
                     onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'code':
      input = <input className={`form-control text-uppercase${invalid}`} id={id} value={value ?? ''}
                     maxLength={field.length ?? field.maxlength} autoComplete="off"
                     onChange={(e) => onChange(e.target.value)} />;
      break;
    default:
      input = <input className={`form-control${invalid}`} id={id} value={value ?? ''} autoComplete="off"
                     maxLength={field.maxlength} onChange={(e) => onChange(e.target.value)} />;
  }

  return <div className={col}>{label}{input}{help}</div>;
}

/* -------------------------------------------------------------------------- */
/* Delete                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Delete asks for a reason because the API requires one, and shows what is
 * referencing the row before the button rather than after the refusal.
 *
 * `GET /{entity}/{id}` carries a `references` breakdown; the list row only
 * carries the total, so this fetches the detail while the dialog is open.
 */
function DeleteModal({ entity, record, onClose, onDeleted }) {
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState(null);

  const detail = useQuery({
    queryKey: queryKeys.masterData.detail(entity.key, record.id),
    queryFn: () => api.getRecord(entity.key, record.id),
  });
  const references = detail.data?.references ?? [];

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const remove = useMutation({
    mutationFn: () => api.deleteRecord(entity.key, record.id, { reason, rowVersion: record.row_version }),
    onSuccess: onDeleted,
    onError: setFailure,
  });

  const blocked = references.length > 0;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete {entity.singular.toLowerCase()}</h5>
              <button type="button" className="btn-close btn-sm" aria-label="Close" onClick={onClose} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); setFailure(null); remove.mutate(); }}>
              <div className="modal-body">
                <p className="t-sm">
                  Delete <span className="fw-650">{record.name ?? record.code}</span>?
                </p>

                {detail.isLoading ? (
                  <Spinner label="Checking what references this" />
                ) : blocked ? (
                  <div className="alert alert-warning py-2 px-3 t-xs" role="alert">
                    <div className="fw-650 mb-1">
                      <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
                      Still in use
                    </div>
                    <ul className="mb-1 ps-3">
                      {references.map((r) => (
                        <li key={`${r.table}.${r.column}`}>{number(r.count)} {r.entity}</li>
                      ))}
                    </ul>
                    Deactivate it instead — it disappears from pickers and existing records keep
                    their value.
                  </div>
                ) : (
                  <p className="t-xs ink-3">
                    Nothing references this record, so removing it changes no existing data.
                  </p>
                )}

                <label className="lbl mt-2" htmlFor="mdDeleteReason">Reason</label>
                <input
                  id="mdDeleteReason"
                  className="form-control"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this being removed?"
                  required
                  autoComplete="off"
                />
                <div className="t-2xs ink-4 mt-1">Recorded in the audit trail.</div>

                {failure && (
                  <div className="alert alert-danger py-2 px-3 t-xs mt-3" role="alert">
                    <i className="bi bi-exclamation-circle me-1" aria-hidden="true" />
                    {failure.message}
                    {failure.payload?.suggestion && (
                      <div className="mt-1">{failure.payload.suggestion}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-delete btn-sm-x"
                  disabled={remove.isPending || !reason.trim() || blocked}
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Form value plumbing                                                         */
/* -------------------------------------------------------------------------- */

/** Seed the form: an existing record, a duplicate of one, or the field defaults. */
function initialValues(entity, mode, record, scopeId) {
  const out = {};
  for (const f of entity.fields) {
    let v = record ? record[f.key] : f.def;
    if (f.type === 'json') v = v ? JSON.stringify(v, null, 2) : '';
    if (mode === 'duplicate') {
      /* A duplicate cannot keep a unique value, and finding out at the 409 is a
         wasted round trip. Nudged here so the field is obviously provisional. */
      if (f.unique && f.type === 'code') v = `${v ?? ''}-COPY`.slice(0, f.length ?? f.maxlength ?? 40);
      else if (f.unique) v = `${v ?? ''} copy`;
    }
    out[f.key] = v ?? (f.type === 'switch' ? false : '');
  }
  /* A new stage created while a pipeline is selected belongs to that pipeline. */
  if (mode !== 'edit' && entity.scoped_by && scopeId) out[entity.scoped_by] = scopeId;
  return out;
}

/**
 * Turn form state into the request body.
 *
 * Two rules matter. Empty strings become `null` for a nullable column and are
 * dropped entirely for one that is not — sending `""` where the column is `NOT
 * NULL` is a 422 the user cannot act on. And on an edit only changed fields are
 * sent, so two people editing different columns of the same row do not overwrite
 * each other with identical values.
 */
function buildPayload(entity, values, existing) {
  const body = {};

  for (const f of entity.fields) {
    const raw = values[f.key];
    let v;

    if (f.type === 'switch') {
      v = Boolean(raw);
    } else if (NUMERIC.has(f.type)) {
      v = raw === '' || raw == null ? null : Number(raw);
    } else if (f.type === 'json') {
      if (!String(raw ?? '').trim()) v = null;
      else {
        try { v = JSON.parse(raw); } catch { v = undefined; }
      }
    } else if (f.type === 'code') {
      v = String(raw ?? '').trim().toUpperCase();
    } else {
      v = typeof raw === 'string' ? raw.trim() : raw;
    }

    if (v === '' || v === undefined) v = null;

    if (v === null && !f.nullable) continue;      // the column would reject it
    if (existing && sameValue(existing[f.key], v, f)) continue;  // unchanged

    body[f.key] = v;
  }

  return body;
}

function sameValue(before, after, field) {
  if (field.type === 'json') return JSON.stringify(before ?? null) === JSON.stringify(after ?? null);
  if (NUMERIC.has(field.type)) return Number(before ?? NaN) === Number(after ?? NaN) || (before == null && after == null);
  return (before ?? null) === (after ?? null);
}

/* -------------------------------------------------------------------------- */
/* Export                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Download the visible rows as CSV.
 *
 * The prototype logs this to the console; here it is a real file, because an
 * administrator asked to export a picklist wants the picklist, not a toast
 * describing one. Reference columns export the label rather than the uuid — the
 * id is meaningless in a spreadsheet.
 */
function exportCsv(entity, rows, dispatch) {
  if (!entity || !rows.length) return;

  const columns = entity.columns;
  const cell = (row, c) => {
    const f = entity.fields.find((x) => x.key === c);
    const v = f?.type === 'ref' ? row.labels?.[c] : row[c];
    return v == null ? '' : String(v);
  };
  const escape = (s) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

  const csv = [
    columns.map((c) => escape(entity.fields.find((f) => f.key === c)?.label ?? c)).join(','),
    ...rows.map((r) => columns.map((c) => escape(cell(r, c))).join(',')),
  ].join('\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entity.key}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  dispatch(pushToast({
    tone: 'success',
    message: `Exported ${rows.length} ${entity.label.toLowerCase()}.`,
    detail: `${entity.key}.csv · ${columns.length} columns`,
  }));
}
